<template>
  <a-form
    class="flex flex-col w-full"
    :model="formState"
    :name="formName"
    layout="vertical"
    autocomplete="on"
    @finish="onFinish"
    @finishFailed="onFinishFailed"
  >
    <div class="self-center mb-3">
      <router-link :to="{ name: ROUTE_NAMES.HOME }">
        <img
          alt="san-ha"
          class="object-contain w-40 h-40"
          src="@/assets/images/san-ha-logo.png"
        />
      </router-link>
    </div>
    <div>
      <p class="text-4xl text-center">ระบบรับสมัครออนไลน์</p>
      <p class="text-3xl text-center">
        ผู้ดำรงตำแหน่งในองค์กรตามรัฐธรรมนูญและกฏหมาย
      </p>
      <p class="text-3xl text-center">สำนักงานเลขาธิการวุฒิสภา</p>
    </div>
    <div class="grid h-12 grid-cols-2 my-8 font-medium">
      <a-button
        :type="userType === 1 ? 'primary' : 'default'"
        class="rounded-e-none !border-e-0"
        :class="userType === 1 && 'pointer-events-none'"
        @click="() => handleChangeUserType(1)"
      >
        บุคคลทั่วไป
      </a-button>
      <a-button
        :type="userType === 2 ? 'primary' : 'default'"
        class="rounded-s-none !border-s-0"
        :class="userType === 2 && 'pointer-events-none'"
        @click="() => handleChangeUserType(2)"
      >
        คณะกรรมการสรรหา
      </a-button>
    </div>
    <div class="flex flex-col gap-3 mb-8">
      <BaseInput
        :label="userType === 1 ? `อีเมล์` : `ชื่อผู้ใช้`"
        name="email"
        placeholder=""
        v-model="formState.email"
        required
      />
      <BaseInput
        label="รหัสผ่าน"
        name="password"
        placeholder=""
        type="password"
        v-model="formState.password"
        required
      />
      <div class="flex flex-col items-end">
        <router-link
          :to="{ name: ROUTE_NAMES.FORGOT_PASSWORD }"
          class="inline-flex items-center gap-1 text-xl font-medium cursor-pointer tag-a"
        >
          ลืมรหัสผ่าน?
        </router-link>
      </div>
    </div>
    <a-button
      type="primary"
      class="w-full"
      html-type="submit"
      :disabled="isLoading"
    >
      <a-spin v-if="isLoading" class="mt-[10px]" />
      <span v-else>เข้าสู่ระบบ</span>
    </a-button>
    <a-divider orientation="center" class="!my-2">
      <span class="text-xl text-grey-2">หรือ</span>
    </a-divider>
    <router-link :to="{ name: ROUTE_NAMES.SUBSCRIPTION }">
      <a-button class="w-full gap-1"> ลงทะเบียนเข้าใช้งาน </a-button>
    </router-link>
  </a-form>
</template>

<script setup lang="ts">
import { postSignIn, postSignInSSO } from "@/apis/register/post";
import { PostSignInResponse } from "@/apis/register/post/type";
import BaseInput from "@/components/formControls/BaseInput.vue";
import { useLoading } from "@/composables/useLoading";
import { ROUTE_NAMES } from "@/router/constants";
import { IdentityStore } from "@/stores/type";
import { debugConsole } from "@/utils/helper";
import { message } from "ant-design-vue";
import { inject, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import type { LoginFormState } from "./type";

const router = useRouter();
const formName = "login-form";
const userType = ref<number>(1);
const { isLoading, disableButton, enableButton } = useLoading();

const formTargetStore = inject<IdentityStore>("formTargetStore");

const formState = reactive<LoginFormState>({
  email: "",
  password: "",
});

function handleChangeUserType(value: number) {
  userType.value = value;
  resetForm();
}

const onFinish = async (values: LoginFormState) => {
  try {
    disableButton();
    debugConsole(values, `${formName} success`);

    const requestDetails =
      userType.value === 1
        ? {
            apiCall: postSignIn,
            payload: { email: values.email, password: values.password },
            routeName: ROUTE_NAMES.HOME,
            errorMsg: "อีเมล์หรือรหัสผ่านไม่ถูกต้อง",
          }
        : {
            apiCall: postSignInSSO,
            payload: { username: values.email, password: values.password },
            routeName: ROUTE_NAMES.APPLICANT_INFORMATION,
            errorMsg: "ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง",
          };

    const response = await requestDetails.apiCall(
      requestDetails.payload as any,
    );

    handleResponse(response, requestDetails.routeName, requestDetails.errorMsg);
  } catch (error) {
    message.error("เกิดข้อผิดพลาดระหว่างการประมวลผล");
    resetForm();
  } finally {
    enableButton();
  }
};

function handleResponse(
  response: PostSignInResponse,
  routeName: string,
  errorMsg: string,
) {
  const { status, statusText } = response;

  switch (status) {
    case 200:
      message.success("เข้าสู่ระบบสำเร็จ");
      const query = formTargetStore?.state.id
        ? { tab: 2, id: formTargetStore.state.id }
        : {};
      router.push({ name: routeName, query });
      break;
    case 401:
      message.error(errorMsg);
      resetForm();
      break;
    case 402:
      message.error(statusText || "ข้อผิดพลาด");
      resetForm();
      break;
    default:
      message.error("ระบบขัดข้อง");
      resetForm();
      break;
  }
}

function onFinishFailed(errorInfo: any) {
  debugConsole(errorInfo, `${formName} failed`);
  message.warn("ผู้ใช้กรอกแบบฟอร์มไม่สมบูรณ์");
}

function resetForm() {
  formState.email = "";
  formState.password = "";
}
</script>
